/**
 * composeShellBootstrapContext — builds the minimal shell context consumed by
 * the production shell state machine after both data lanes have resolved.
 *
 * Status derivation:
 *   ready             — plugin available + browser-owned gateway session available
 *   needs_auth_recovery — session error code is CREDENTIALS_MISSING,
 *                         GATEWAY_SESSION_UNAVAILABLE, or
 *                         INSTALLATION_NOT_FOUND (operator can retry recovery)
 *   degraded          — any other session error OR plugin unavailable
 *
 * routeShopId source priority:
 *   1. session.installation.internalShopId  (gateway-assigned, canonical)
 *   2. String(plugin.context.shopId || '')  (PS shop_id, fallback only)
 *
 * @param {object} pluginLane  - Result from readPluginLane
 * @param {object} sessionLane - Result from readBrowserGatewaySessionLane
 * @returns {object} Shell bootstrap context
 */

import { AUTH_RECOVERY_SESSION_CODES } from './gatewaySessionErrors';

// ---------------------------------------------------------------------------
// Status derivation
// ---------------------------------------------------------------------------

function deriveStatus(pluginLane, sessionLane) {
  if (!pluginLane.available) {
    return 'degraded';
  }
  if (sessionLane.available) {
    return 'ready';
  }
  const code = sessionLane.error?.code;
  if (AUTH_RECOVERY_SESSION_CODES.has(code)) {
    return 'needs_auth_recovery';
  }
  return 'degraded';
}

// ---------------------------------------------------------------------------
// routeShopId resolution
// ---------------------------------------------------------------------------

function resolveRouteShopId(pluginLane, sessionLane) {
  const gwShopId = sessionLane.installation?.internalShopId;
  if (gwShopId) return gwShopId;
  return String(pluginLane.context?.shopId || '');
}

// ---------------------------------------------------------------------------
// Composer
// ---------------------------------------------------------------------------

export function composeShellBootstrapContext(pluginLane, sessionLane) {
  const inst = sessionLane.installation || {};

  return {
    status: deriveStatus(pluginLane, sessionLane),

    // Route / identity fields
    routeShopId: resolveRouteShopId(pluginLane, sessionLane),
    gatewayToken: inst.token || null,
    prestashopShopId: pluginLane.context?.shopId || null,
    adminLanguageId: pluginLane.context?.adminLanguageId ?? null,
    internalShopId: inst.internalShopId || null,
    platform: String(inst.platform || pluginLane.context?.platform || '').trim().toLowerCase() || null,
    moduleVersion: pluginLane.context?.moduleVersion
      || inst.moduleVersion
      || sessionLane.identity?.moduleVersion
      || null,
    domain: inst.domain || null,
    storefrontUrl: pluginLane.context?.shopUrl || null,

    // Feature capabilities map — provided by the plugin lane at bootstrap time.
    // Capabilities are fail-closed: an absent key is treated as unsupported.
    capabilities: pluginLane.capabilities ?? {},

    // Wizard completion flag sourced from onboarding.state plugin bridge response.
    // null  → unknown (bootstrap fallback path or older plugin) — treated as fail-open.
    // false → wizard not yet completed → ShellBootstrapEntry redirects to /setup.
    // true  → wizard done → redirect to /home as usual.
    onboardingWizardCompleted: pluginLane.onboarding?.wizardCompleted ?? null,

    // Gateway auth fields
    gatewayAccessToken: sessionLane.accessToken || null,
    gatewayTokenExpiresAt: sessionLane.expiresAt ?? null,
    // Base URL of the gateway — forwarded from the plugin bridge session response.
    // Stored in session so the iframe can build the gateway client without
    // cross-origin access to window.kealabsBridgeData in the parent page.
    gatewayBaseUrl: sessionLane.gatewayBaseUrl || null,

    // Raw lanes — available to downstream consumers without re-fetching
    plugin: pluginLane,
    session: sessionLane,
  };
}
